import { datadogRum } from "@datadog/browser-rum";

import { createStorageKey } from "@/utils/storage";

/**
 * Makes each point where the service worker update path can stall visible in
 * RUM: state as the `sw` global context, failures as `sw_*` actions. A broken
 * update path does not error, it goes quiet.
 */

// Covers only the worker script fetch; the precache downloads after register() settles.
export const REGISTER_TIMEOUT_MS = 15_000;

// Loads in a row that find a worker waiting; each reloads, so two can be one interrupted reload.
export const STUCK_WAITING_THRESHOLD = 3;

export const WAITING_LOADS_STORAGE_KEY = createStorageKey(
  "service-worker-waiting-loads",
);

type RegisterState = "pending" | "registered" | "failed";

type ServiceWorkerContext = {
  supported: boolean;
  controlledAtBoot: boolean;
  register?: RegisterState;
  registerMs?: number;
  waitingAtBoot?: boolean;
  installingAtBoot?: boolean;
};

let context: ServiceWorkerContext | undefined;

function publish(patch: Partial<ServiceWorkerContext>) {
  context = { supported: false, controlledAtBoot: false, ...context, ...patch };
  try {
    datadogRum.setGlobalContextProperty("sw", context);
  } catch {
    // Reporting must never interfere with registration.
  }
}

function report(name: string, attributes: Record<string, unknown> = {}) {
  try {
    datadogRum.addAction(name, attributes);
  } catch {
    // Reporting must never interfere with registration.
  }
}

function describeError(error: unknown) {
  return error instanceof Error
    ? { errorName: error.name, errorMessage: error.message }
    : { errorMessage: String(error) };
}

export function recordBoot() {
  const supported = "serviceWorker" in navigator;
  publish({
    supported,
    controlledAtBoot: supported && navigator.serviceWorker.controller != null,
  });
}

export async function trackRegistration<T>(register: () => Promise<T>) {
  const startedAt = performance.now();
  const elapsedMs = () => Math.round(performance.now() - startedAt);
  publish({ register: "pending" });
  const timeout = window.setTimeout(() => {
    report("sw_register_timeout", { timeoutMs: REGISTER_TIMEOUT_MS });
  }, REGISTER_TIMEOUT_MS);

  try {
    const result = await register();
    publish({ register: "registered", registerMs: elapsedMs() });
    return result;
  } catch (error) {
    publish({ register: "failed", registerMs: elapsedMs() });
    report("sw_register_failed", describeError(error));
    throw error;
  } finally {
    window.clearTimeout(timeout);
  }
}

export function recordRegistration(registration: ServiceWorkerRegistration) {
  const waiting = registration.waiting != null;
  publish({
    waitingAtBoot: waiting,
    installingAtBoot: !!registration.installing,
  });

  try {
    if (!waiting) {
      localStorage.removeItem(WAITING_LOADS_STORAGE_KEY);
      return;
    }
    const previous = Number(localStorage.getItem(WAITING_LOADS_STORAGE_KEY));
    const consecutiveLoads = (Number.isFinite(previous) ? previous : 0) + 1;
    localStorage.setItem(WAITING_LOADS_STORAGE_KEY, String(consecutiveLoads));
    if (consecutiveLoads >= STUCK_WAITING_THRESHOLD) {
      report("sw_stuck_waiting", { consecutiveLoads });
    }
  } catch {
    // localStorage unavailable (private browsing, quota). Nothing to count.
  }
}

// `installing` straight to `redundant` is a failed install (precache fetch or
// quota); redundant after `installed` is a worker superseded by a newer one.
export function watchInstallOutcome(worker: ServiceWorker) {
  let reachedInstalled = worker.state !== "installing";
  const onStateChange = () => {
    if (worker.state === "redundant") {
      if (!reachedInstalled) {
        report("sw_install_failed", { scriptURL: worker.scriptURL });
      }
      worker.removeEventListener("statechange", onStateChange);
      return;
    }
    if (worker.state !== "installing") {
      reachedInstalled = true;
    }
    if (worker.state === "activated") {
      worker.removeEventListener("statechange", onStateChange);
    }
  };
  worker.addEventListener("statechange", onStateChange);
}

export function reportUpdateCheckFailed(error: unknown) {
  report("sw_update_check_failed", describeError(error));
}
